import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { createContext, use, useCallback, useMemo, useState, type ReactNode } from 'react'

type Toast = { id: number; message: string; severity: 'success' | 'info' | 'warning' | 'error' }

type ToasterApi = { notify: (message: string, severity?: Toast['severity']) => void }

const ToasterContext = createContext<ToasterApi | null>(null)

/**
 * Avisos passageiros.
 *
 * O `Snackbar` do MUI já publica em `aria-live="polite"`, então um caso novo
 * chegando pelo polling é anunciado ao leitor de tela sem roubar o foco de
 * quem está preenchendo o wizard.
 */
export function ToasterProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null)

  const notify = useCallback((message: string, severity: Toast['severity'] = 'info') => {
    setToast({ id: Date.now(), message, severity })
  }, [])

  const api = useMemo(() => ({ notify }), [notify])

  return (
    <ToasterContext value={api}>
      {children}

      <Snackbar
        open={toast !== null}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToasterContext>
  )
}

export function useToaster(): ToasterApi {
  const context = use(ToasterContext)

  if (!context) throw new Error('useToaster precisa estar dentro de <ToasterProvider>')

  return context
}
